import React, { useState, useEffect } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "../../../../utils/toast";
import PageLoader from "../../../shared/PageLoader";
import "../common/EditDetails.css";
import { useGetUsers } from "../../../../backend/users";
import { getUserDetails, hasUserAccess } from "../../../../backend/auth";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import { useGetActs } from "../../../../backend/masters";
import { useGetLitigationCategories, useCreateCategory } from "../../../../backend/litigation";
import { formatDateForInput } from "../../../../backend/common/noticeManagementUtils";
import { DropdownIndicator, CustomMenuList } from "../common/TableComponents";
import { useDocumentUpload } from "../common/useDocumentUpload";
import cloudUpload from "./../../../../assets/img/icon/cloudUpload.svg";
import downloadButton from "./../../../../assets/img/icon/downloadButton.svg";
import deleteIcon from "./../../../../assets/img/icon/deleteIcon.svg";

interface EditDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: any;
  onSave?: (data: any, section?: string) => void;
  isLoading?: boolean;
  onDocumentDelete?: () => void;
}

const LitigationsEditDetails: React.FC<EditDetailsProps> = ({
  isOpen,
  onClose,
  initialData = {},
  onSave,
  isLoading = false,
  onDocumentDelete,
}) => {
  const user = getUserDetails();

  const [formData, setFormData] = useState<any>({
    name: "",
    description: "",
    issued_date: "",
    resolution_date: "",
    category_id: "",
    act_id: "",
    assigned_to: "",
    watcher_ids: [],
    court_name: "",
    case_number: "",
    next_hearing_date: "",
    advocate_details: "",
    case_status: "",
  });

  const [initialFormData, setInitialFormData] = useState<any>({});
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  // Document upload hook
  const documentUpload = useDocumentUpload({
    resourceType: "litigation",
    resourceId: initialData.id,
    initialDocuments: initialData.documents || [],
    onDocumentDelete,
  });

  // Category management
  const { categories: fetchedCategories, refetch: refetchCategories } = useGetLitigationCategories(
    initialData.company_id,
    !!(initialData.company_id)
  );
  const categories = fetchedCategories || [];
  const [showCategoryPopup, setShowCategoryPopup] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  // Auto-select latest category
  useEffect(() => {
    if (categories.length > 0 && !formData.category_id) {
      setFormData((prev: any) => ({
        ...prev,
        category_id: categories[0].id,
      }));
    }
  }, [categories]);

  const { createCategory, creating: creatingCategory } = useCreateCategory(
    (data: any) => {
      toast.success("Category added successfully");
      const newCategory = data && data.data ? data.data : null;
      if (newCategory && newCategory.id) {
        setFormData((prev: any) => ({
          ...prev,
          category_id: newCategory.id,
        }));
      }
      refetchCategories();
      setNewCategoryName("");
      setShowCategoryPopup(false);
    },
    (error: any) => {
      const errorData = error && error.response && error.response.data;
      const errorMessage =
        (errorData && errorData.error_messages && errorData.error_messages[0]) ||
        (errorData && errorData.message) ||
        "Failed to create category";
      toast.error(errorMessage);
    }
  );

  // Fetch Acts
  const { acts = [] } = useGetActs({}, isOpen);

  // Fetch Litigation Owners
  const { records: fetchedLitigationOwners = [] } = useGetUsers(
    {
      filters: {
        companies_id_in: [initialData.associate_company_id],
        locations_id_in: [initialData.location_id],
        roles_name_in: ["Notice Owner"],
      },
    },
    isOpen && Boolean(initialData.associate_company_id && initialData.location_id)
  );

  // Deduplicate owners by ID
  const litigationOwners = Array.from(
    new Map(fetchedLitigationOwners.map((owner: any) => [owner.id, owner])).values()
  );

  // Fetch Litigation Watchers
  const { records: fetchedLitigationWatchers = [] } = useGetUsers(
    {
      filters: {
        companies_id_in: [initialData.associate_company_id],
        locations_id_in: [initialData.location_id],
        roles_name_in: ["Notice Watcher"],
      },
    },
    isOpen && Boolean(initialData.associate_company_id && initialData.location_id)
  );

  // Deduplicate watchers by ID
  const litigationWatchers = Array.from(
    new Map(fetchedLitigationWatchers.map((watcher: any) => [watcher.id, watcher])).values()
  );

  // Handle close with reset
  const handleClose = () => {
    setFormData({
      name: "",
      description: "",
      issued_date: "",
      resolution_date: "",
      category_id: "",
      act_id: "",
      assigned_to: "",
      watcher_ids: [],
      court_name: "",
      case_number: "",
      next_hearing_date: "",
      advocate_details: "",
      case_status: "",
    });
    setInitialFormData({});
    setHasChanges(false);
    setNewCategoryName("");
    setShowCategoryPopup(false);
    documentUpload.resetDocuments();
    onClose();
  };


  // Load initial data
  useEffect(() => {
    if (isOpen && initialData && Object.keys(initialData).length > 0) {
      // Only initialize once per panel open (don't reset on refetch)
      if (Object.keys(initialFormData).length === 0) {
        const formDataSnapshot = {
          name: initialData.name || "",
          description: initialData.description || "",
          issued_date: formatDateForInput(initialData.issued_date) || "",
          resolution_date: formatDateForInput(initialData.resolution_date) || "",
          category_id: initialData.category_id || "",
          act_id: initialData.act_id || "",
          assigned_to: initialData.assigned_to || "",
          watcher_ids: initialData.watcher_ids || [],
          court_name: initialData.court_name || "",
          case_number: initialData.case_number || "",
          next_hearing_date: formatDateForInput(initialData.next_hearing_date) || "",
          advocate_details: initialData.advocate_details || "",
          case_status: initialData.case_status || "",
        };

        setFormData(formDataSnapshot);
        setInitialFormData(JSON.parse(JSON.stringify(formDataSnapshot)));

        // Load existing documents
        documentUpload.loadDocuments(initialData.documents || []);
      }
    }
  }, [initialData, isOpen]);

  // Track changes
  useEffect(() => {
    if (Object.keys(initialFormData).length === 0) {
      setHasChanges(false);
      return;
    }

    const formChanged = Object.keys(formData).some((key) => {
      const current = formData[key];
      const initial = initialFormData[key];
      return JSON.stringify(current) !== JSON.stringify(initial);
    });

    const documentsChanged = documentUpload.hasDocumentsChanged();

    setHasChanges(formChanged || documentsChanged);
  }, [formData, initialFormData, documentUpload]);

  const handleInputChange = (field: string, value: any) => {
    const trimmedValue = typeof value === "string" ? value.trimStart() : value;

    setFormData((prev: any) => ({
      ...prev,
      [field]: trimmedValue,
    }));
  };

  const handleCategoryChange = (value: string) => {
    if (value === "add_new") {
      const companyId = formData.company_id || initialData.company_id;
      if (!companyId) {
        toast.error("Company information not available");
        return;
      }
      setShowCategoryPopup(true);
      setFormData((prev: any) => ({ ...prev, category_id: "" }));
    } else {
      handleInputChange("category_id", value);
    }
  };

  const handleSaveCategory = () => {
    if (!newCategoryName || newCategoryName.trim() === "") {
      toast.error("Please enter category name");
      return;
    }

    const companyId = formData.company_id || initialData.company_id;
    const payload = {
      companyId: companyId,
      name: newCategoryName.trim(),
      category_type: "litigation",
      company_id: companyId,
    };

    createCategory(payload as any);
  };

  const handleCloseCategoryPopup = () => {
    setNewCategoryName("");
    setShowCategoryPopup(false);
  };

  const validateForm = () => {
    if (!formData.name || formData.name.trim() === "") {
      toast.error("Please enter litigation name");
      return false;
    }
    if (formData.name.length > 250) {
      toast.error("Litigation name cannot exceed 250 characters");
      return false;
    }
    if (!formData.description || formData.description.trim() === "") {
      toast.error("Please enter description");
      return false;
    }
    if (formData.description.length > 1000) {
      toast.error("Description cannot exceed 1000 characters");
      return false;
    }
    // if (!formData.resolution_date) {
    //   toast.error("Please select resolution date");
    //   return false;
    // }
    if (!formData.category_id) {
      toast.error("Please select category");
      return false;
    }
    if (!formData.act_id) {
      toast.error("Please select issued under act");
      return false;
    }
    if (hasUserAccess(USER_PRIVILEGES.NOTICE_MANAGER)) {
      if (!formData.assigned_to) {
        toast.error("Please select assigned to");
        return false;
      }
      if (!formData.watcher_ids || formData.watcher_ids.length === 0) {
        toast.error("Please select at least one watcher");
        return false;
      }
    }
    if (!formData.court_name || formData.court_name.trim() === "") {
      toast.error("Please enter court name");
      return false;
    }
    if (!formData.case_number || formData.case_number.trim() === "") {
      toast.error("Please enter case number");
      return false;
    }
    if (!formData.next_hearing_date) {
      toast.error("Please select next hearing date");
      return false;
    }
    if (!formData.advocate_details || formData.advocate_details.trim() === "") {
      toast.error("Please enter advocate details");
      return false;
    }
    if (formData.advocate_details.length > 1000) {
      toast.error("Advocate details cannot exceed 1000 characters");
      return false;
    }
    if (!formData.case_status || formData.case_status.trim() === "") {
      toast.error("Please enter case status");
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    // Build payload with litigation and document_details
    const payload: any = {};

    // Only include changed form fields in litigation object
    const changedFields: any = {};
    Object.keys(formData).forEach((key) => {
      if (JSON.stringify(formData[key]) !== JSON.stringify(initialFormData[key])) {
        changedFields[key] = formData[key];
      }
    });

    // Always include litigation object, even if empty
    payload.litigation = changedFields;

    // Include newly uploaded documents in document_details array
    if (documentUpload.uploadedDocuments.length > 0) {
      payload.document_details = documentUpload.uploadedDocuments.map((doc: any) => ({
        url: doc.file_path || doc.url,
        s3_key: doc.s3_key,
        s3_bucket: doc.s3_bucket,
        s3_region: doc.s3_region,
        file_info: {
          filename: doc.name,
          size: doc.file_size,
          content_type: doc.content_type
        }
      }));
    }

    if (onSave) {
      onSave(payload);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="licensedetails-wrapper">
        <div className="license-popup-overlay">
          <div className="license-popup">
            <div className="popup-header d-flex justify-content-between align-items-start">
              <div>
                <h6 className="fw-semibold fs-5 m-0">Edit: {initialData.name}</h6>
              </div>
              <button
                className="btn-close mt-0"
                aria-label="Close"
                onClick={handleClose}
              ></button>
            </div>
            <hr className="mt-0" />
            <div className="popup-body">
              <div className="edit-container">
                <label className="mb-1 text-md">
                  Litigation Name<span>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter litigation name"
                  maxLength={64}
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                />
              </div>

              <div className="edit-container">
                <label className="mb-1 text-md">
                  Description<span>*</span>
                </label>
                <textarea
                  className="form-control"
                  placeholder="Enter description"
                  maxLength={1000}
                  rows={4}
                  style={{ minHeight: "114px" }}
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                ></textarea>
                <small className="text-muted text-sm d-flex justify-content-end">
                  {formData.description.length}/1000 characters
                </small>
              </div>

              <div className="edit-container">
                <label className="mb-1 text-md">
                  Resolution Date
                </label>
                <div style={{ position: 'relative' }}>
                  <DatePicker
                    selected={formData.resolution_date ? new Date(formData.resolution_date) : null}
                    onChange={(date: Date | null) => {
                      if (date) {
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        handleInputChange("resolution_date", `${year}-${month}-${day}`);
                      } else {
                        handleInputChange("resolution_date", "");
                      }
                    }}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select date"
                    className="form-control"
                    wrapperClassName="w-100"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={50}
                    minDate={initialData.issued_date ? new Date(initialData.issued_date) : null}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#1E1E1E"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none'
                    }}
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </div>
              </div>

              <div className="edit-container">
                <label className="mb-1 text-md">
                  Category<span>*</span>
                </label>
                <Select
                  placeholder="Select category"
                  options={[
                    ...(categories || []).map((category: any) => ({
                      value: category.id,
                      label: category.name
                    })),
                    { value: "add_new", label: "+ Add new category" }
                  ]}
                  onChange={(option: any) => handleCategoryChange(option ? option.value : "")}
                  value={[
                    ...(categories || []).map((c: any) => ({ value: c.id, label: c.name })),
                    { value: "add_new", label: "+ Add new category" }
                  ].find((c: any) => c.value === formData.category_id) || null}
                  className="select-control"
                  components={{ DropdownIndicator, MenuList: CustomMenuList }}
                  isSearchable={true}
                  isClearable={false}
                />
              </div>

              <div className="edit-container">
                <label className="mb-1 text-md">
                  Issued Under Act<span>*</span>
                </label>
                <Select
                  placeholder="Select act"
                  options={(acts || []).map((act: any) => ({
                    value: act.id,
                    label: `${act.establishment_type} - ${act.name}`
                  }))}
                  onChange={(option: any) => handleInputChange("act_id", option ? option.value : "")}
                  value={(acts || []).map((a: any) => ({ value: a.id, label: `${a.establishment_type} - ${a.name}` })).find((a: any) => a.value === formData.act_id) || null}
                  className="select-control"
                  components={{ DropdownIndicator }}
                  isSearchable={true}
                  isClearable={false}
                />
              </div>

              {hasUserAccess(USER_PRIVILEGES.NOTICE_MANAGER) && (
                <>
                  <div className="edit-container">
                    <label className="mb-1 text-md">
                      Assigned To<span>*</span>
                    </label>
                    <Select
                      placeholder="Select user"
                      options={(litigationOwners || []).map((owner: any) => ({
                        value: owner.id,
                        label: owner.name
                      }))}
                      onChange={(option: any) => handleInputChange("assigned_to", option ? option.value : "")}
                      value={(litigationOwners || []).map((o: any) => ({ value: o.id, label: o.name })).find((o: any) => o.value === formData.assigned_to) || null}
                      className="select-control"
                      components={{ DropdownIndicator }}
                      isSearchable={true}
                      isClearable={false}
                    />
                  </div>

                  <div className="edit-container">
                    <label className="mb-1 text-md">
                      Watchers<span>*</span>
                    </label>
                <div className="watchers-box p-3"
                  style={{ maxHeight: "200px", overflowY: "auto" }}
                >
                  {(litigationWatchers || []).map((watcher: any) => (
                    <div
                      key={watcher.id}
                      className="mb-3 d-flex gap-2 align-items-start"
                    >
                      <input
                        className="form-check"
                        type="checkbox"
                        id={`watcher-${watcher.id}`}
                        checked={formData.watcher_ids.includes(watcher.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData((prev: any) => ({
                            ...prev,
                            watcher_ids: checked
                              ? [...prev.watcher_ids, watcher.id]
                              : prev.watcher_ids.filter((id: string) => id !== watcher.id),
                          }));
                        }}
                      />
                      <label
                        className="form-check-label mb-0"
                        htmlFor={`watcher-${watcher.id}`}
                      >
                        <div className="fw-medium">{watcher.name}</div>
                        <div className="text-muted small">{watcher.email}</div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
                </>
              )}

              <div className="edit-container">
                <label className="mb-1 text-md">
                  Court Name<span>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter court name"
                  maxLength={64}
                  value={formData.court_name}
                  onChange={(e) => handleInputChange("court_name", e.target.value)}
                />
              </div>

              <div className="edit-container">
                <label className="mb-1 text-md">
                  Case Number<span>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter case number"
                  maxLength={64}
                  value={formData.case_number}
                  onChange={(e) => handleInputChange("case_number", e.target.value)}
                />
              </div>

              <div className="edit-container">
                <label className="mb-1 text-md">
                  Next Hearing Date<span>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <DatePicker
                    selected={formData.next_hearing_date ? new Date(formData.next_hearing_date) : null}
                    onChange={(date: Date | null) => {
                      if (date) {
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        handleInputChange("next_hearing_date", `${year}-${month}-${day}`);
                      } else {
                        handleInputChange("next_hearing_date", "");
                      }
                    }}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select date"
                    className="form-control"
                    wrapperClassName="w-100"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={50}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#1E1E1E"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none'
                    }}
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </div>
              </div>

              <div className="edit-container">
                <label className="mb-1 text-md">
                  Advocate Details<span>*</span>
                </label>
                <textarea
                  className="form-control"
                  placeholder="Enter advocate details"
                  maxLength={1000}
                  rows={4}
                  style={{ height: "80px" }}
                  value={formData.advocate_details}
                  onChange={(e) => handleInputChange("advocate_details", e.target.value)}
                ></textarea>
                <small className="text-muted text-sm d-flex justify-content-end">
                  {formData.advocate_details.length}/1000 characters
                </small>
              </div>

              <div className="edit-container">
                <label className="mb-1 text-md">
                  Case Status<span>*</span>
                </label>
                <textarea
                  className="form-control"
                  placeholder="Enter case status"
                  maxLength={1000}
                  rows={4}
                  style={{ height: "80px" }}
                  value={formData.case_status}
                  onChange={(e) => handleInputChange("case_status", e.target.value)}
                ></textarea>
                <small className="text-muted text-sm d-flex justify-content-end">
                  {formData.case_status.length}/1000 characters
                </small>
              </div>

              <div className="edit-container">
                <label className="mb-1 text-md">Upload Litigation Documents</label>
                <input
                  ref={documentUpload.fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.pdf"
                  onChange={documentUpload.handleFileSelect}
                  style={{ display: "none" }}
                  multiple
                />

                {(documentUpload.existingDocuments.length + documentUpload.uploadedFiles.length) < 5 && (
                  <div
                    className="p-4 text-center upload-box mb-3"
                    onClick={documentUpload.handleChooseFile}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="mb-2">
                      <img
                        src={cloudUpload}
                        alt="cloudUpload"
                        className="me-2"
                      />
                    </div>
                    <p className="mb-2 text-md">
                      Click to browse and upload
                    </p>
                    <button
                      type="button"
                      className="mb-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        documentUpload.handleChooseFile();
                      }}
                    >
                      Choose File
                    </button>
                    <div>
                      <small className="text-gray-12 d-block">
                        Supports up to 5 files (max 5 MB each): JPG, JPEG, PNG, DOC, DOCX, XLS, XLSX, PDF
                      </small>
                    </div>
                  </div>
                )}

                {documentUpload.existingDocuments.length > 0 && (
                  <div className="mb-3">
                    <label className="mb-2 text-sm fw-semibold">Existing Documents</label>
                    {documentUpload.existingDocuments.map((document: any, index: number) => (
                      <div
                        key={document.id || index}
                        className="upload-successful position-relative mb-3"
                      >
                        <div className="p-4 d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center gap-2" style={{width: "90%"}}>
                            <p className="text-sm m-0 ellipse">{document.name}</p>
                          </div>
                          <div className="d-flex gap-3 align-items-center">
                            <div
                              className=""
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                documentUpload.handleDeleteExistingDocument(document);
                              }}
                            >
                              <img
                                className="d-block"
                                src={deleteIcon}
                                alt="deleteIcon"
                              />
                            </div>
                            <div
                              style={{
                                cursor: "pointer",
                                height: "25px",
                                width: "25px",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                documentUpload.handleDownloadExisting(document);
                              }}
                            >
                              <img
                                className="d-block"
                                src={downloadButton}
                                alt="downloadButton"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {documentUpload.uploadedFiles.length > 0 && (
                  <div className="mb-3">
                    <label className="mb-2 text-sm fw-semibold">Newly Uploaded Documents</label>
                    {documentUpload.uploadedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="upload-successful position-relative mb-3"
                      >
                        <div
                          className="close-button position-absolute"
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            documentUpload.handleRemoveFile(index);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M10.0002 5.99992L6.00016 9.99992M6.00016 5.99992L10.0002 9.99992M14.6668 7.99992C14.6668 11.6818 11.6821 14.6666 8.00016 14.6666C4.31826 14.6666 1.3335 11.6818 1.3335 7.99992C1.3335 4.31802 4.31826 1.33325 8.00016 1.33325C11.6821 1.33325 14.6668 4.31802 14.6668 7.99992Z"
                              stroke="#757575"
                              strokeWidth="1.33333"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div className="p-4 d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center gap-2" style={{width: "90%"}}>
                            <p className="text-sm m-0 ellipse">{file.name}</p>
                          </div>
                          <div
                            style={{
                              cursor: "pointer",
                              height: "25px",
                              width: "25px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              documentUpload.handleDownloadNew(file);
                            }}
                          >
                            <img
                              className="d-block"
                              src={downloadButton}
                              alt="downloadButton"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {documentUpload.isUploading && (
                  <div className="text-center text-muted">
                    <small>Uploading files...</small>
                  </div>
                )}
              </div>
            </div>

            <hr />
            <div className="d-flex gap-3 w-100">
              <button
                type="button"
                className="button-secondary w-100 fw-semibold"
                onClick={handleClose}
                disabled={isLoading || documentUpload.isUploading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button-primary w-100 fw-semibold"
                onClick={handleSubmit}
                disabled={isLoading || documentUpload.isUploading || !hasChanges}
              >
                {isLoading ? "Saving..." : documentUpload.isUploading ? "Uploading..." : "Submit"}
              </button>
            </div>
          </div>
          {(isLoading || documentUpload.isUploading) && <PageLoader />}
        </div>
      </div>

      {/* Add New Category Popup */}
      {showCategoryPopup && (
        <div className="cost-details-form-wrapper">
          <div className="license-popup-overlay" onClick={handleCloseCategoryPopup}></div>
          <div className="cost-details-popup" style={{ maxWidth: "600px" }}>
            <div className="popup-header d-flex justify-content-between align-items-start">
              <div>
                <h5 className="mb-2 fw-semibold">Add New Category</h5>
                <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                  This action will create a new litigation category which will be saved in the category list henceforth.
                </p>
              </div>
              <button
                className="btn-close"
                aria-label="Close"
                onClick={handleCloseCategoryPopup}
              ></button>
            </div>

            <hr className="my-3" />

            <div className="popup-body">
              <div className="mb-3">
                <label className="form-label">
                  New Category Name<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Add new category name here"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  maxLength={100}
                />
              </div>
            </div>

            <hr className="my-3" />

            <div className="d-flex gap-3 w-100">
              <button
                type="button"
                className="button-secondary w-100 fw-semibold"
                onClick={handleCloseCategoryPopup}
                disabled={creatingCategory}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button-primary w-100 fw-semibold"
                onClick={handleSaveCategory}
                disabled={creatingCategory || !newCategoryName.trim()}
              >
                {creatingCategory ? "Saving..." : "Save Category"}
              </button>
            </div>
          </div>
          {creatingCategory && <PageLoader />}
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {documentUpload.showDeleteConfirm && (
        <div className="cost-details-form-wrapper">
          <div className="license-popup-overlay" onClick={documentUpload.cancelDeleteDocument}></div>
          <div className="cost-details-popup" style={{ maxWidth: "600px" }}>
            <div className="popup-header d-flex justify-content-between align-items-start">
                <h5 className="fw-semibold mb-0">Delete Document</h5>
              <button
                className="btn-close"
                aria-label="Close"
                onClick={documentUpload.cancelDeleteDocument}
              ></button>
            </div>

            <hr className="mt-0 mb-3" />

            <div className="popup-body">
              <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                Are you sure you want to delete this file?
              </p>
            </div>

            <hr className="my-3" />

            <div className="popup-footer d-flex gap-3">
              <button
                type="button"
                className="button-secondary px-4 w-50"
                onClick={documentUpload.cancelDeleteDocument}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button-primary px-4 w-50"
                onClick={documentUpload.confirmDeleteDocument}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LitigationsEditDetails;
